import { useForm } from 'react-hook-form';

import { Alert, Button, TextField } from '@shared';

import { useAuth } from '@/features/auth/useAuth';
import { useTranslations } from '@/i18n/client';
import { PasswordField } from './PasswordField';

/** Deliberately permissive: the Portal is the authority on address validity. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LoginFormValues {
  email: string;
  password: string;
}

export interface LoginFormProps {
  /** Called after a successful sign-in (navigation is the caller's concern). */
  onSuccess?: () => void;
}

export function LoginForm({ onSuccess }: LoginFormProps) {
  const t = useTranslations('auth.login');
  const tStatus = useTranslations('common.status');
  const tErrors = useTranslations('errors.api');
  const { login, busy, error, clearError } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({ defaultValues: { email: '', password: '' } });

  async function onSubmit(values: LoginFormValues): Promise<void> {
    clearError();
    const ok = await login({ email: values.email.trim(), password: values.password });
    if (ok) {
      onSuccess?.();
    }
  }

  return (
    <form className="md-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {error ? <Alert variant="error">{tErrors(error)}</Alert> : null}

      <TextField
        label={t('emailLabel')}
        type="email"
        autoComplete="username"
        invalid={Boolean(errors.email)}
        {...(errors.email?.message ? { message: errors.email.message } : {})}
        {...register('email', {
          required: t('errors.emailRequired'),
          pattern: { value: EMAIL_PATTERN, message: t('errors.emailInvalid') },
        })}
      />

      <PasswordField
        label={t('passwordLabel')}
        autoComplete="current-password"
        invalid={Boolean(errors.password)}
        showLabel={t('showPasswordLabel')}
        hideLabel={t('hidePasswordLabel')}
        {...(errors.password?.message ? { message: errors.password.message } : {})}
        {...register('password', { required: t('errors.passwordRequired') })}
      />

      <Button type="submit" className="max-sm:w-full" loading={busy} loadingLabel={tStatus('working')}>
        {t('submitButton')}
      </Button>
    </form>
  );
}
